import { useEffect, useMemo, useState } from 'react';
import type { BECurrentMatchdayWindow } from '@/lib/network';
import {
  getTeamUpdatingWindow,
  isWithinTeamUpdatingWindow,
  type TeamUpdatingWindow,
} from './teamUpdatingWindow';

/**
 * How often the window is re-checked. The only boundary is a gameweek
 * deadline, so minute-level accuracy is enough - this exists so a page left
 * open over the deadline starts showing the pop-up without a reload.
 */
export const TEAM_UPDATING_WINDOW_TICK_MS = 30_000;

export interface UseTeamUpdatingWindowResult {
  updatingWindow: TeamUpdatingWindow | null;
  isUpdating: boolean;
}

export const useTeamUpdatingWindow = (
  matchdayWindow?: BECurrentMatchdayWindow | null
): UseTeamUpdatingWindowResult => {
  const updatingWindow = useMemo(() => getTeamUpdatingWindow(matchdayWindow), [matchdayWindow]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!updatingWindow) {
      return;
    }

    // Re-read the clock straight away: the window can arrive long after mount,
    // by which point the `now` from the first render is stale.
    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), TEAM_UPDATING_WINDOW_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [updatingWindow]);

  return {
    updatingWindow,
    isUpdating: isWithinTeamUpdatingWindow(updatingWindow, now),
  };
};
